import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { MoneyInput } from '@nucleus-commerce/admin-kit/forms';
import type { ManualPaymentMethod } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Switch } from '@nucleus-commerce/ui/components/switch';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import {
  emptyPaymentMethodForm,
  fromPaymentMethod,
  toPaymentMethodCreate,
  toPaymentMethodUpdate,
} from '../api/mappers';
import {
  useCreatePaymentMethod,
  useUpdatePaymentMethod,
} from '../hooks/use-payment-method-mutations';
import {
  paymentMethodFormSchema,
  type PaymentMethodFormInput,
  type PaymentMethodFormOutput,
} from '../schemas/payment-method-form-schema';
import { paymentMethodErrorTarget } from '../schemas/payment-method-rules';

const KNOWN_FIELDS: ReadonlySet<string> = new Set([
  'key',
  'name',
  'description',
  'instructions',
  'fee',
  'minOrder',
  'maxOrder',
  'isActive',
  'position',
]);

/** `body.fee.currency` belongs to the fee field. */
const mapField = (field: string) => field.replace(/\.currency$|\.amount$/, '');

export interface PaymentMethodDialogProps {
  storeId: string;
  currency: string;
  /** null creates; a method edits it (its key is locked). */
  method: ManualPaymentMethod | null;
  /** Proposed position for a new method: after the last one. */
  nextPosition: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PaymentMethodDialog({
  storeId,
  currency,
  method,
  nextPosition,
  open,
  onOpenChange,
}: PaymentMethodDialogProps) {
  const { t } = useTranslation(['paymentMethods', 'common']);
  const create = useCreatePaymentMethod(storeId);
  const update = useUpdatePaymentMethod(storeId);
  const message = useErrorMessage();
  const form = useForm<PaymentMethodFormInput, unknown, PaymentMethodFormOutput>({
    resolver: useI18nResolver(paymentMethodFormSchema),
    defaultValues: method
      ? fromPaymentMethod(method)
      : emptyPaymentMethodForm(currency, nextPosition),
  });
  const isPending = create.isPending || update.isPending;

  const close = () => {
    onOpenChange(false);
    create.reset();
    update.reset();
  };

  const submit = form.handleSubmit(async (values) => {
    try {
      if (method) {
        await update.mutateAsync({ id: method.id, body: toPaymentMethodUpdate(values, currency) });
      } else {
        await create.mutateAsync(toPaymentMethodCreate(values));
      }
      close();
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS, mapField })) return;
      const target = paymentMethodErrorTarget(error);
      form.setError(target?.field ?? 'root.server', {
        type: 'server',
        message: target ? t(target.key, target.values) : message(error),
      });
    }
  });

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="max-h-[calc(100svh-2rem)] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {method ? t('paymentMethods:edit.title') : t('paymentMethods:create.title')}
          </DialogTitle>
          <DialogDescription>
            {method ? t('paymentMethods:edit.description') : t('paymentMethods:create.description')}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            {form.formState.errors.root?.server?.message ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
              </Alert>
            ) : null}
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="key"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('paymentMethods:fields.key')}</FormLabel>
                    <FormControl>
                      <Input
                        autoComplete="off"
                        className="font-mono"
                        placeholder="bank_transfer"
                        disabled={Boolean(method)}
                        readOnly={Boolean(method)}
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>{t('paymentMethods:fields.keyHint')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('paymentMethods:fields.name')}</FormLabel>
                    <FormControl>
                      <Input autoComplete="off" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('paymentMethods:fields.description')}</FormLabel>
                  <FormControl>
                    <Input autoComplete="off" {...field} />
                  </FormControl>
                  <FormDescription>{t('paymentMethods:fields.descriptionHint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="instructions"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('paymentMethods:fields.instructions')}</FormLabel>
                  <FormControl>
                    <Textarea rows={4} {...field} />
                  </FormControl>
                  <FormDescription>{t('paymentMethods:fields.instructionsHint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid gap-4 sm:grid-cols-3">
              <FormField
                control={form.control}
                name="fee"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>{t('paymentMethods:fields.fee')}</FormLabel>
                    <FormControl>
                      <MoneyInput
                        currency={currency}
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        aria-invalid={fieldState.invalid}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="minOrder"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>{t('paymentMethods:fields.minOrder')}</FormLabel>
                    <FormControl>
                      <MoneyInput
                        currency={currency}
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        aria-invalid={fieldState.invalid}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="maxOrder"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>{t('paymentMethods:fields.maxOrder')}</FormLabel>
                    <FormControl>
                      <MoneyInput
                        currency={currency}
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        aria-invalid={fieldState.invalid}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <p className="text-muted-foreground -mt-2 text-xs">
              {t('paymentMethods:fields.feeHint')} {t('paymentMethods:fields.limitsHint')}
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="position"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('paymentMethods:fields.position')}</FormLabel>
                    <FormControl>
                      <Input type="number" inputMode="numeric" min={0} step={1} {...field} />
                    </FormControl>
                    <FormDescription>{t('paymentMethods:fields.positionHint')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="isActive"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between gap-4 rounded-md border p-3">
                    <FormLabel>{t('paymentMethods:fields.isActive')}</FormLabel>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={close}>
                {t('common:actions.cancel')}
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending
                  ? t('common:actions.saving')
                  : method
                    ? t('common:actions.save')
                    : t('paymentMethods:create.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
